import type { ProviderConfig } from '../types';
import { serverReady } from './server';

// BYOK keys live in the browser (localStorage) — the accepted relaxation for a
// serverless hosted app with no per-user server keychain. They are sent per
// request to our stateless proxy Worker, which never stores or logs them, and
// the UI only ever renders a masked fingerprint — never the full key back.

const KEYS_LS = 'ielts.keys.v1';
const PROVIDERS_LS = 'ielts.providers.v1';

export type KeyName = 'openai' | 'elevenlabs';
export type KeyStore = Partial<Record<KeyName, string>>;

export function loadKeys(): KeyStore {
  try {
    return JSON.parse(localStorage.getItem(KEYS_LS) || '{}');
  } catch {
    return {};
  }
}

export function saveKey(name: KeyName, value: string): void {
  const k = loadKeys();
  const trimmed = value.trim();
  if (trimmed) k[name] = trimmed;
  else delete k[name];
  localStorage.setItem(KEYS_LS, JSON.stringify(k));
}

export function removeKey(name: KeyName): void {
  const k = loadKeys();
  delete k[name];
  localStorage.setItem(KEYS_LS, JSON.stringify(k));
}

/** Masked fingerprint, e.g. `sk-pr…4f2a`. Never expose the full key. */
export function fingerprint(value: string): string {
  const v = value.trim();
  if (v.length <= 8) return '••••';
  return `${v.slice(0, 5)}…${v.slice(-4)}`;
}

export const DEFAULT_BASE_URL = 'https://api.openai.com/v1';
export const DEFAULT_TTS_MODEL = 'gpt-4o-mini-tts';
export const DEFAULT_STT_MODEL = 'whisper-1';

export function loadProviders(): ProviderConfig {
  const def: ProviderConfig = {
    tts: 'workers', // server voice by default — real examiner voice, no key
    stt: 'workers',
    openaiBaseUrl: DEFAULT_BASE_URL,
    ttsModel: DEFAULT_TTS_MODEL,
    sttModel: DEFAULT_STT_MODEL,
  };
  try {
    const saved = JSON.parse(localStorage.getItem(PROVIDERS_LS) || '{}');
    const merged = { ...def, ...saved };
    // Guard against blanked-out fields from older/partial saves.
    if (!merged.openaiBaseUrl) merged.openaiBaseUrl = DEFAULT_BASE_URL;
    if (!merged.ttsModel) merged.ttsModel = DEFAULT_TTS_MODEL;
    if (!merged.sttModel) merged.sttModel = DEFAULT_STT_MODEL;
    return merged;
  } catch {
    return def;
  }
}

export function isCustomEndpoint(): boolean {
  return loadProviders().openaiBaseUrl.replace(/\/+$/, '') !== DEFAULT_BASE_URL;
}

export function saveProviders(p: Partial<ProviderConfig>): void {
  const cur = loadProviders();
  localStorage.setItem(PROVIDERS_LS, JSON.stringify({ ...cur, ...p }));
}

export type TtsProvider = 'workers' | 'openai' | 'elevenlabs' | 'browser';

/**
 * Which TTS path will actually run, given saved keys + provider choice + server
 * availability. Falls back to the server voice, then the browser voice.
 */
export function effectiveTts(): { provider: TtsProvider; usingFallback: boolean } {
  const { tts } = loadProviders();
  const keys = loadKeys();
  if (tts === 'browser') return { provider: 'browser', usingFallback: false };
  if (tts === 'openai' && keys.openai) return { provider: 'openai', usingFallback: false };
  if (tts === 'elevenlabs' && keys.elevenlabs) return { provider: 'elevenlabs', usingFallback: false };
  if (tts === 'workers' && serverReady()) return { provider: 'workers', usingFallback: false };
  // Chosen provider isn't usable — prefer the server voice, else the browser voice.
  if (serverReady()) return { provider: 'workers', usingFallback: true };
  return { provider: 'browser', usingFallback: true };
}

/** Which STT path will run: BYOK key wins, else the server transcription. */
export function effectiveStt(): 'openai' | 'workers' | null {
  const { stt } = loadProviders();
  const hasKey = !!loadKeys().openai;
  if (stt === 'openai' && hasKey) return 'openai';
  if (hasKey) return 'openai';
  if (serverReady()) return 'workers';
  return null;
}

export function sttAvailable(): boolean {
  return effectiveStt() !== null;
}
